import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  Button,
} from "@mui/material";
import ViewWeekRoundedIcon from "@mui/icons-material/ViewWeekRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import CompareArrowsRoundedIcon from "@mui/icons-material/CompareArrowsRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import Seo from "../../components/Seo";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";

export default function MultibandCompressionLearning() {
  const { style } = useStyle();
  const [height, setHeight] = useState(window.innerHeight);
  const topContainer = useRef(null);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    topContainer.current?.scrollIntoView({ block: "start" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";
  const pageUrl = "https://parishaudio.com/learning/multi-band-compression";
  const pageTitle = "Multi-Band Compression in Music Production: What It Is and How It Works";
  const pageDescription =
    "Learn what multi-band compression is, how it compresses separate frequency ranges, how it differs from standard compression and dynamic EQ, and how it is used in mixing and mastering.";

  const schema = useMemo(
    () => [
      {
        "@context": "https://schema.org",
        "@type": "Article",
        "@id": `${pageUrl}#article`,
        headline: pageTitle,
        description: pageDescription,
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
        },
        url: pageUrl,
        inLanguage: "en-US",
        articleSection: "Audio Education",
        keywords: [
          "multiband compression",
          "multi band compressor",
          "what is multiband compression",
          "multiband compressor vs dynamic eq",
          "mastering multiband compression",
          "frequency dependent compression",
        ],
        author: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
        },
        publisher: {
          "@type": "Organization",
          name: "Parish Audio",
          url: "https://parishaudio.com",
          logo: {
            "@type": "ImageObject",
            url: "https://parishaudio.com/assets/logo.png",
          },
        },
        image: {
          "@type": "ImageObject",
          url: "https://parishaudio.com/RecordingStudioStockImage1.jpg",
        },
        about: [
          { "@type": "Thing", name: "Multiband compression" },
          { "@type": "Thing", name: "Compression" },
          { "@type": "Thing", name: "Dynamic EQ" },
          { "@type": "Thing", name: "Mastering" },
          { "@type": "Thing", name: "Frequency-dependent processing" },
          { "@type": "Thing", name: "Music production" },
        ],
        isPartOf: {
          "@type": "CollectionPage",
          name: "Parish Audio Learning Center",
          url: "https://parishaudio.com/learning",
        },
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "@id": `${pageUrl}#breadcrumbs`,
        itemListElement: [
          {
            "@type": "ListItem",
            position: 1,
            name: "Home",
            item: "https://parishaudio.com",
          },
          {
            "@type": "ListItem",
            position: 2,
            name: "Learning Center",
            item: "https://parishaudio.com/learning",
          },
          {
            "@type": "ListItem",
            position: 3,
            name: "Multi-Band Compression",
            item: pageUrl,
          },
        ],
      },
    ],
    []
  );

  const quickFacts = [
    {
      title: "What it does",
      text: "Multi-band compression splits audio into separate frequency ranges and compresses each band independently.",
      icon: <ViewWeekRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Why it matters",
      text: "It gives more precise control than standard compression when only certain parts of the spectrum need dynamic control.",
      icon: <AutoAwesomeRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Where it is used",
      text: "Mastering, vocal control, bass management, de-harshing, low-end tightening, mix buses, and problem-solving in dense mixes.",
      icon: <GraphicEqRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const mbConcepts = [
    {
      name: "Frequency Split",
      description:
        "The signal is divided into multiple frequency bands using crossover points before compression is applied.",
    },
    {
      name: "Independent Compression",
      description:
        "Each band has its own threshold, ratio, attack, release, and gain behavior, allowing more targeted control.",
    },
    {
      name: "Selective Dynamics",
      description:
        "Instead of compressing the full signal equally, you can control only the lows, only the highs, or any other band that needs attention.",
    },
    {
      name: "Recombined Output",
      description:
        "After each band is processed, the bands are summed back together into a full-range signal.",
    },
  ];

  const commonUses = [
    {
      name: "Low-End Control",
      description:
        "Tightens kick and bass energy without flattening the rest of the mix.",
    },
    {
      name: "Harshness Management",
      description:
        "Controls aggressive upper mids or highs only when they become too strong.",
    },
    {
      name: "Vocal Stability",
      description:
        "Helps keep chest, presence, or sibilance ranges under better control than full-band compression alone.",
    },
    {
      name: "Mastering Refinement",
      description:
        "Makes small, frequency-specific dynamic corrections on a full mix without overprocessing everything else.",
    },
    {
      name: "Bass Definition",
      description:
        "Keeps sub energy stable while preserving upper articulation and note clarity.",
    },
    {
      name: "Problem Solving",
      description:
        "Useful when only one region of the spectrum misbehaves, but the rest of the source is fine.",
    },
  ];

  const controls = [
    {
      name: "Crossover Frequency",
      description:
        "Sets where one band ends and the next begins, which determines what frequency region each compressor is controlling.",
    },
    {
      name: "Threshold",
      description:
        "Sets the level where compression begins within a particular band.",
    },
    {
      name: "Ratio",
      description:
        "Controls how strongly that band is compressed once it crosses threshold.",
    },
    {
      name: "Attack and Release",
      description:
        "Shape how quickly each band responds and recovers, which strongly affects feel and transparency.",
    },
    {
      name: "Band Gain / Makeup",
      description:
        "Lets you rebalance the output of each band after compression.",
    },
    {
      name: "Solo / Audition",
      description:
        "Lets you hear an individual band by itself, which makes setup and troubleshooting much easier.",
    },
  ];

  const comparisons = [
    {
      name: "Standard Compression",
      description:
        "Compresses the whole signal together, which is simpler and often more musical when broad control is all you need.",
    },
    {
      name: "Multi-Band Compression",
      description:
        "Compresses different frequency areas independently, which is more targeted but also easier to overdo.",
    },
    {
      name: "Dynamic EQ",
      description:
        "Usually works more like moving EQ bands that react dynamically, often with narrower and more surgical control than a multiband compressor.",
    },
  ];

  const bestPractices = [
    "Use multi-band compression only when full-band compression is not solving the real problem well enough.",
    "Keep crossover choices intentional, because awkward crossover placement can create unnatural behavior.",
    "Make small moves first. Multi-band compression can become overprocessed very quickly.",
    "Listen carefully for tonal changes as well as dynamic changes, because aggressive settings can make the sound feel uneven.",
    "Compare often with bypass and level-match when possible so you do not mistake louder or darker for better.",
  ];

  const relatedLinks = [
    {
      title: "Compression",
      to: "/learning/compression",
      description:
        "Start with standard compression if you want the core dynamics concepts before moving into multiband processing.",
    },
    {
      title: "De-Esser",
      to: "/learning/de-esser",
      description:
        "See how de-essing relates to targeted dynamic control in a narrower frequency area.",
    },
    {
      title: "Equalization",
      to: "/learning/equalization",
      description:
        "Learn how EQ shapes tone statically, while multiband compression shapes certain frequency ranges dynamically.",
    },
    {
      title: "Mastering Basics",
      to: "/learning/mastering-basics",
      description:
        "Explore where multi-band compression fits into a mastering workflow and when it can be useful.",
    },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="Multi-Band Compression | Parish Audio Learning"
        description={pageDescription}
        canonical={pageUrl}
        schema={schema}
      />

      <Box
        ref={topContainer}
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 260, md: 310 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.84) 0%, rgba(8,12,18,0.74) 38%, rgba(8,12,18,0.42) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 760 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Learning Center"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.2rem", md: "3.35rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      Multi-Band Compression in Music Production
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      Multi-band compression is a more targeted form of compression that works
                      on separate frequency ranges independently. Instead of compressing the whole
                      signal at once, it lets you control the lows, mids, and highs separately.
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 700,
                      }}
                    >
                      That makes it powerful for solving problems that full-band compression
                      cannot solve cleanly, such as boomy low end, harsh upper mids, unstable
                      vocal ranges, or mastering issues where only one part of the spectrum
                      needs dynamic control.
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, color: style.color4, mb: 0.5, lineHeight: 1.2 }}>
                          {item.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.55 }}>
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    What is multi-band compression?
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Multi-band compression is a dynamics process where the audio spectrum is split
                    into separate bands, and each band is compressed independently. This allows you
                    to control only the parts of the signal that need attention, rather than forcing
                    the entire signal to react together.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    For example, a vocal may have harshness only in the upper mids, or a mix may
                    have low-end energy that jumps out too much. A multiband compressor can target
                    those areas without flattening the full signal.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    That flexibility is what makes multi-band compression so useful, but also why
                    it can be easy to misuse if applied too heavily.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    How multi-band compression works
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {mbConcepts.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: accent, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>

                  <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                    Because each band reacts separately, you can keep the low end stable while
                    leaving the midrange more open, or control a harsh top end without dulling
                    the entire signal.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Common uses for multi-band compression
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {commonUses.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                          {item.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {item.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompareArrowsRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Multi-band compression vs standard compression and dynamic EQ
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.2}>
                      <Typography sx={{ color: style.color4, fontWeight: 700 }}>
                        Similar goals, different level of precision
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        Standard compression usually sounds more cohesive and straightforward when
                        you want broad dynamic control. Multi-band compression is more surgical and
                        frequency-aware. Dynamic EQ often goes even narrower and more selective,
                        especially when only a specific problem band needs movement.
                      </Typography>

                      <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                        A good rule is to use the simplest tool that solves the problem cleanly.
                        If full-band compression works, use that. If only one area misbehaves,
                        multi-band compression or dynamic EQ may make more sense.
                      </Typography>
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TuneRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Common multi-band compressor controls
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {controls.map((item) => (
                      <Paper key={item.name} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TuneRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.4 }}>
                              {item.name}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {item.description}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <LibraryMusicRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        How multi-band compression is used in practice
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      One of the biggest advantages of multi-band compression is that it can solve
                      spectral dynamic problems that regular compression often makes worse. If a full
                      compressor clamps down every time the low end hits hard, the whole mix may pump.
                      A multiband compressor can control just the low band instead.
                    </Typography>

                    <Typography sx={{ color: style.color3, lineHeight: 1.85 }}>
                      The same logic applies to harsh upper mids, unstable sibilance regions, or
                      inconsistent bass tone. It is a problem-solving tool as much as a creative one.
                    </Typography>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <WarningAmberRoundedIcon sx={{ color: accent }} />
                      <Typography sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}>
                        Common multi-band compression mistakes
                      </Typography>
                    </Stack>

                    <Stack spacing={1.35}>
                      {[
                        "Using it everywhere instead of only where a real frequency-specific problem exists.",
                        "Applying too much compression across multiple bands and making the sound feel overcontrolled or lifeless.",
                        "Choosing awkward crossover points that split the natural body of the sound in unnatural places.",
                        "Using multiband compression when EQ, standard compression, or better arrangement choices would solve the issue more cleanly.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <WarningAmberRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={2}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Best practices for multi-band compression
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "stretch",
                    }}
                  >
                    {bestPractices.map((item, index) => (
                      <Paper key={index} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Stack direction="row" spacing={1.1} alignItems="flex-start">
                          <TipsAndUpdatesRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <SpeedRoundedIcon sx={{ color: accent }} />
                    <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                      Why multi-band compression is so powerful
                    </Typography>
                  </Stack>

                  <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                    <Stack spacing={1.35}>
                      {[
                        "It solves dynamic problems that only happen in part of the spectrum.",
                        "It can preserve the rest of the sound better than full-band compression in certain situations.",
                        "It is extremely useful in mastering and bus work when subtle frequency-dependent control is needed.",
                        "It can shape both consistency and tonal balance at the same time.",
                      ].map((item) => (
                        <Stack key={item} direction="row" spacing={1.1} alignItems="flex-start">
                          <SpeedRoundedIcon sx={{ color: accent, mt: "2px", flexShrink: 0 }} />
                          <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                            {item}
                          </Typography>
                        </Stack>
                      ))}
                    </Stack>
                  </Paper>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Related learning topics
                  </Typography>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 2,
                      alignItems: "start",
                    }}
                  >
                    {relatedLinks.map((item) => (
                      <Box
                        key={item.title}
                        sx={{
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "flex-start",
                        }}
                      >
                        <Paper
                          elevation={0}
                          sx={{
                            ...subtlePanelSx,
                            p: 1.5,
                            width: "100%",
                            minHeight: "75%",
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "space-between",
                          }}
                        >
                          <Box>
                            <Typography sx={{ color: style.color4, fontWeight: 700, mb: 0.75 }}>
                              {item.title}
                            </Typography>
                            <Typography sx={{ color: style.color3, lineHeight: 1.75, mb: 2 }}>
                              {item.description}
                            </Typography>
                          </Box>

                          <Box>
                            <Button
                              component={RouterLink}
                              to={item.to}
                              sx={{
                                borderRadius: "5px",
                                textTransform: "none",
                                fontWeight: 700,
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.05)",
                                border: `1px solid ${accent}`,
                                "&:hover": {
                                  borderColor: style.color4,
                                  backgroundColor: "rgba(255,255,255,0.10)",
                                },
                              }}
                            >
                              Read More
                            </Button>
                          </Box>
                        </Paper>
                      </Box>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Typography sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}>
                    Final thoughts
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    Multi-band compression is powerful because it lets you control dynamics with
                    much more precision than standard compression. It can fix frequency-specific
                    issues, refine mastering decisions, and help difficult sources sit more cleanly.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.9 }}>
                    The tradeoff is complexity. It works best when used with a clear reason, small
                    moves, and careful listening. When used well, it can solve problems that would
                    otherwise be frustrating or hard to manage.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.25, md: 1.5 } }}>
                <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.7 }}>
                  * We are not affiliated with any brands or artists mentioned on this site. *
                </Typography>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}